import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';

// Layout
import PublicLayout from './layouts/PublicLayout';

// Main Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';

// Company Pages
import OurStory from './pages/company/OurStory';
import LeadershipTeam from './pages/company/LeadershipTeam';
import GlobalPresence from './pages/company/GlobalPresence';
import CareersPage from './pages/company/CareersPage';
import CultureValues from './pages/company/CultureValues';
import Partnerships from './pages/company/Partnerships';
import NewsPress from './pages/company/NewsPress';
import Awards from './pages/company/Awards';

// Services Pages
import ServicesOverview from './pages/services/ServicesOverview';
import AIMachineLearning from './pages/services/AIMachineLearning';
import CustomSoftware from './pages/services/CustomSoftware';
import DataAnalytics from './pages/services/DataAnalytics';
import CloudImplementation from './pages/services/CloudImplementation';
import PromptEngineering from './pages/services/PromptEngineering';
import StrategicConsultation from './pages/services/StrategicConsultation';
import SystemIntegration from './pages/services/SystemIntegration';
import ProcessAutomation from './pages/services/ProcessAutomation';
import DigitalTransformation from './pages/services/DigitalTransformation';
import CyberSecurity from './pages/services/CyberSecurity';
import ManagedITServices from './pages/services/ManagedITServices';
import QualityAssurance from './pages/services/QualityAssurance';

// Industry Solutions Pages
import SolutionsOverview from './pages/solutions/SolutionsOverview';
import HealthcareLifeSciences from './pages/solutions/HealthcareLifeSciences';
import FinancialServices from './pages/solutions/FinancialServices';
import ManufacturingLogistics from './pages/solutions/ManufacturingLogistics';
import RetailEcommerce from './pages/solutions/RetailEcommerce';
import TechnologySoftware from './pages/solutions/TechnologySoftware';
import GovernmentPublicSector from './pages/solutions/GovernmentPublicSector';
import EducationTraining from './pages/solutions/EducationTraining';
import EnergyUtilities from './pages/solutions/EnergyUtilities';
import MediaEntertainment from './pages/solutions/MediaEntertainment';
import RealEstate from './pages/solutions/RealEstate';
import Telecommunications from './pages/solutions/Telecommunications';
import Automotive from './pages/solutions/Automotive';

// AI Hub Pages
import AIHub from './pages/ai-hub/AIHub';
import IntelligentAutomation from './pages/ai-hub/IntelligentAutomation';
import PredictiveAnalytics from './pages/ai-hub/PredictiveAnalytics';
import NaturalLanguageProcessing from './pages/ai-hub/NaturalLanguageProcessing';
import ComputerVision from './pages/ai-hub/ComputerVision';
import IntelligentChatbots from './pages/ai-hub/IntelligentChatbots';
import MLOpsFramework from './pages/ai-hub/MLOpsFramework';
import AIEthicsGovernance from './pages/ai-hub/AIEthicsGovernance';
import GenerativeAI from './pages/ai-hub/GenerativeAI';
import AIReadinessAssessment from './pages/ai-hub/AIReadinessAssessment';

// Legal & Compliance Pages
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';
import CookiePolicy from './pages/legal/CookiePolicy';
import DataSecurity from './pages/legal/DataSecurity';
import ComplianceCertifications from './pages/legal/ComplianceCertifications';
import ServiceLevelAgreement from './pages/legal/ServiceLevelAgreement';
import AcceptableUsePolicy from './pages/legal/AcceptableUsePolicy';
import GDPR from './pages/legal/GDPR';

// Events Pages
// import EventsPage from './pages/events/EventsPage';
// import EventDetails from './pages/events/EventDetails';
// import ConferencesPage from './pages/events/ConferencesPage';
// import WorkshopsPage from './pages/events/WorkshopsPage';

// Utility Pages
import SearchResults from './pages/SearchResults';
import NotFoundPage from './pages/errors/NotFoundPage';
import ServerErrorPage from './pages/errors/ServerErrorPage';
import MaintenancePage from './pages/errors/MaintenancePage';

function App() {
  return (
    <>
      <Router>
        <Toaster richColors position="top-right" />
        <Routes>
          {/* All routes wrapped in PublicLayout */}
          <Route path="/" element={<PublicLayout />}>
            {/* Main Pages */}
            <Route index element={<HomePage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="search" element={<SearchResults />} />

            {/* Company Routes */}
            <Route path="company">
              <Route path="our-story" element={<OurStory />} />
              <Route path="leadership" element={<LeadershipTeam />} />
              <Route path="global-presence" element={<GlobalPresence />} />
              <Route path="careers" element={<CareersPage />} />
              <Route path="culture-values" element={<CultureValues />} />
              <Route path="partnerships" element={<Partnerships />} />
              <Route path="news-press" element={<NewsPress />} />
              <Route path="awards" element={<Awards />} />
            </Route>

            {/* Services Routes */}
            <Route path="services">
              <Route index element={<ServicesOverview />} />
              <Route
                path="ai-machine-learning"
                element={<AIMachineLearning />}
              />
              <Route path="custom-software" element={<CustomSoftware />} />
              <Route path="data-analytics" element={<DataAnalytics />} />
              <Route
                path="cloud-implementation"
                element={<CloudImplementation />}
              />
              <Route
                path="prompt-engineering"
                element={<PromptEngineering />}
              />
              <Route
                path="strategic-consultation"
                element={<StrategicConsultation />}
              />
              <Route
                path="system-integration"
                element={<SystemIntegration />}
              />
              <Route
                path="process-automation"
                element={<ProcessAutomation />}
              />
              <Route
                path="digital-transformation"
                element={<DigitalTransformation />}
              />
              <Route path="cybersecurity" element={<CyberSecurity />} />
              <Route
                path="managed-it-services"
                element={<ManagedITServices />}
              />
              <Route path="quality-assurance" element={<QualityAssurance />} />
            </Route>

            {/* Industry Solutions Routes */}
            <Route path="solutions">
              <Route index element={<SolutionsOverview />} />
              <Route
                path="healthcare-life-sciences"
                element={<HealthcareLifeSciences />}
              />
              <Route
                path="financial-services"
                element={<FinancialServices />}
              />
              <Route
                path="manufacturing-logistics"
                element={<ManufacturingLogistics />}
              />
              <Route path="retail-ecommerce" element={<RetailEcommerce />} />
              <Route
                path="technology-software"
                element={<TechnologySoftware />}
              />
              <Route
                path="government-public-sector"
                element={<GovernmentPublicSector />}
              />
              <Route
                path="education-training"
                element={<EducationTraining />}
              />
              <Route path="energy-utilities" element={<EnergyUtilities />} />
              <Route
                path="media-entertainment"
                element={<MediaEntertainment />}
              />
              <Route path="real-estate" element={<RealEstate />} />
              <Route
                path="telecommunications"
                element={<Telecommunications />}
              />
              <Route path="automotive" element={<Automotive />} />
            </Route>

            {/* AI Hub Routes */}
            <Route path="ai-hub">
              <Route index element={<AIHub />} />
              <Route
                path="intelligent-automation"
                element={<IntelligentAutomation />}
              />
              <Route
                path="predictive-analytics"
                element={<PredictiveAnalytics />}
              />
              <Route path="nlp" element={<NaturalLanguageProcessing />} />
              <Route path="computer-vision" element={<ComputerVision />} />
              <Route
                path="intelligent-chatbots"
                element={<IntelligentChatbots />}
              />
              <Route path="mlops-framework" element={<MLOpsFramework />} />
              <Route
                path="ai-ethics-governance"
                element={<AIEthicsGovernance />}
              />
              <Route path="generative-ai" element={<GenerativeAI />} />
              <Route path="ai-readiness" element={<AIReadinessAssessment />} />
            </Route>

            {/* Events Routes */}
            {/* <Route path="events">
              <Route index element={<EventsPage />} />
              <Route path=":eventId" element={<EventDetails />} />
              <Route path="conferences" element={<ConferencesPage />} />
              <Route path="workshops" element={<WorkshopsPage />} />
            </Route> */}

            {/* Legal & Compliance Routes */}
            <Route path="legal">
              <Route path="privacy" element={<PrivacyPolicy />} />
              <Route path="terms" element={<TermsOfService />} />
              <Route path="cookies" element={<CookiePolicy />} />
              <Route path="data-security" element={<DataSecurity />} />
              <Route path="compliance" element={<ComplianceCertifications />} />
              <Route path="sla" element={<ServiceLevelAgreement />} />
              <Route path="aup" element={<AcceptableUsePolicy />} />
              <Route path="gdpr" element={<GDPR />} />
            </Route>
          </Route>

          {/* Error Pages - Outside of PublicLayout for custom error styling */}
          <Route path="/error" element={<ServerErrorPage />} />
          <Route path="/maintenance" element={<MaintenancePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </>
  );
}

export default App;
